import type { LucideIcon } from "lucide-react";
import {
  Activity,
  BarChart,
  Building2,
  CalendarDays,
  DollarSign,
  Factory,
  FileText,
  History,
  LayoutDashboard,
  Package,
  Printer,
  Receipt,
  SlidersHorizontal,
  ToggleLeft,
  Users,
  Users2,
} from "lucide-react";

/**
 * La navegacion, porteada de `Sidebar.tsx` de `imprenta` (SPEC 0003, fase 1).
 *
 * **Lo que importa acá no son los links sino los GRUPOS**: `imprenta` los
 * separa en un bloque principal sin titulo, mas "Gestion" y "Configuracion". Y
 * cada grupo se muestra **solo si la persona tiene algun permiso de sus items**,
 * asi que la agrupacion tambien gobierna visibilidad — no es decoracion.
 *
 * Vive en un archivo de datos, aparte del componente, por dos razones: el
 * sidebar de escritorio y el drawer de mobile consumen la MISMA lista —dos
 * copias divergirian—, y asi agregar una pantalla es agregar una fila.
 *
 * **Ocultar no es autorizar** (invariante 5): esto decide que se ve, y cada
 * endpoint aplica su `requirePermission` igual.
 */

export interface ItemNav {
  ruta: string;
  texto: string;
  icono: LucideIcon;
  /** `recurso.accion`. La persona ve el item si tiene ESTE permiso. */
  permiso: string;
}

export interface GrupoNav {
  /** `null` en el bloque principal, que va sin titulo — como en `imprenta`. */
  titulo: string | null;
  items: ItemNav[];
}

export const NAVEGACION: readonly GrupoNav[] = [
  {
    titulo: null,
    items: [
      // Sin permiso: es el resumen de lo que cada uno YA puede ver, y el worker
      // decide que tarjetas manda. Ponerle uno dejaria gente sin ningun destino
      // al entrar a la raiz.
      { ruta: "/", texto: "Inicio", icono: LayoutDashboard, permiso: "" },
      { ruta: "/produccion", texto: "Producción", icono: Printer, permiso: "produccion.read" },
      // `imprenta` lo tiene como su propio item del sidebar debajo de
      // Produccion (`/production/scheduler`), no como una vista mas del
      // tablero. Se respeta esa forma.
      {
        ruta: "/produccion/calendario",
        texto: "Calendario",
        icono: CalendarDays,
        permiso: "produccion.read",
      },
      // «Consultas» y no «Presupuestos»: es como le dice el cliente cuando pide
      // el trabajo, y el nombre que usa `imprenta` en toda su interfaz. En la
      // base y en la API sigue siendo `presupuesto`.
      {
        ruta: "/presupuestos",
        texto: "Consultas",
        icono: FileText,
        permiso: "presupuestos.read",
      },
      { ruta: "/facturacion", texto: "Facturación", icono: Receipt, permiso: "facturacion.read" },
      { ruta: "/clientes", texto: "Clientes", icono: Users, permiso: "clientes.read" },
      // `imprenta` la pone en el bloque principal, antes de Productos, con el
      // MISMO icono que Producción. Acá va `Factory`: dos items con el mismo
      // icono no se distinguen con el sidebar colapsado, que es cuando el icono
      // es lo único que queda.
      { ruta: "/impresoras", texto: "Impresoras", icono: Factory, permiso: "impresoras.read" },
      { ruta: "/productos", texto: "Productos", icono: Package, permiso: "productos.read" },
      { ruta: "/precios", texto: "Lista de Precios", icono: DollarSign, permiso: "precios.read" },
    ],
  },
  {
    titulo: "Gestión",
    items: [
      { ruta: "/reportes", texto: "Reportes", icono: BarChart, permiso: "reportes.negocio" },
      { ruta: "/auditoria", texto: "Auditoría", icono: History, permiso: "auditoria.read" },
    ],
  },
  {
    titulo: "Configuración",
    items: [
      { ruta: "/usuarios", texto: "Usuarios", icono: Users2, permiso: "usuarios.read" },
      { ruta: "/roles", texto: "Roles", icono: Building2, permiso: "roles.read" },
      {
        ruta: "/catalogos",
        texto: "Parámetros",
        icono: SlidersHorizontal,
        permiso: "catalogos.read",
      },
    ],
  },
  {
    titulo: "Sistema",
    items: [
      { ruta: "/flags", texto: "Feature flags", icono: ToggleLeft, permiso: "sistema.config.read" },
      // `imprenta` lo llama "Monitoreo del Sistema". Acá es "Estado": lo que
      // se monitorea allá —CPU, memoria, disco— no existe sobre Workers, y el
      // nombre prometía algo que la pantalla no da (ADR 0032).
      {
        ruta: "/configuracion",
        texto: "Datos de la imprenta",
        icono: Building2,
        permiso: "sistema.config.read",
      },
      { ruta: "/sistema", texto: "Estado", icono: Activity, permiso: "sistema.metricas" },
    ],
  },
];

/**
 * Filtra la navegacion a lo que esta persona puede ver.
 *
 * Un grupo sin items visibles **no se muestra**, ni siquiera su titulo: un
 * encabezado "Configuracion" con nada debajo hace pensar que algo se rompio.
 */
export function navegacionVisible(
  puede: (codigo: string) => boolean,
): { titulo: string | null; items: ItemNav[] }[] {
  return NAVEGACION.map((grupo) => ({
    titulo: grupo.titulo,
    // `permiso: ""` = sin permiso exigido. Es el caso de Inicio, que resume lo
    // que cada uno ya puede ver.
    items: grupo.items.filter((item) => item.permiso === "" || puede(item.permiso)),
  })).filter((grupo) => grupo.items.length > 0);
}

/**
 * Si alguno de los ítems del grupo es la pantalla actual.
 *
 * Decide qué grupo arranca ABIERTO (ADR 0062). Exige la barra en el prefijo por
 * la misma razón que el ítem activo: `/produccion` y `/produccion-vieja` no son
 * la misma sección, y sin eso se abriría el grupo equivocado.
 */
export function grupoContieneRuta(items: readonly ItemNav[], ruta: string): boolean {
  return items.some((item) => ruta === item.ruta || ruta.startsWith(`${item.ruta}/`));
}
